import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { whatsappAuthAPI } from '../services/api';
import './WhatsAppLogin.css';

const WhatsAppLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: phone, 2: otp, 3: name (new user)
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneDisplay = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
    setError('');
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await whatsappAuthAPI.requestOTP(phone);
      setCountdown(60);
      setStep(2);

      // In demo mode, show the OTP
      if (response.data.demoOtp) {
        setDemoOtp(response.data.demoOtp);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Please wait before requesting another OTP');
        setCountdown(err.response.data.retryAfter || 60);
      } else {
        setError(err.response?.data?.error || 'Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
    } else {
      const newOtp = [...otp];
      newOtp[index] = value.replace(/\D/g, '');
      setOtp(newOtp);

      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
    setError('');
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await whatsappAuthAPI.verifyOTP(phone, otpString, name || undefined);

      if (response.data.isNewUser && !name) {
        setIsNewUser(true);
        setStep(3);
        setLoading(false);
        return;
      }

      // Login successful
      onLogin(response.data.user, response.data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP');
      if (err.response?.data?.attemptsRemaining !== undefined) {
        setError(`Invalid OTP. ${err.response.data.attemptsRemaining} attempts remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const otpString = otp.join('');
      const response = await whatsappAuthAPI.verifyOTP(phone, otpString, name);

      onLogin(response.data.user, response.data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setLoading(true);
    setError('');
    setOtp(['', '', '', '', '', '']);

    try {
      const response = await whatsappAuthAPI.requestOTP(phone);
      setCountdown(60);
      if (response.data.demoOtp) {
        setDemoOtp(response.data.demoOtp);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="whatsapp-login">
      <div className="whatsapp-login-container">
        {/* Header */}
        <div className="whatsapp-login-header">
          <div className="whatsapp-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
              <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="2" fill="none"/>
            </svg>
          </div>
          <h1>Welcome to ONTAP</h1>
          <p>Sign in with your WhatsApp number</p>
        </div>

        {/* Step 1: Phone Number */}
        {step === 1 && (
          <form onSubmit={handleRequestOTP} className="whatsapp-form">
            <div className="phone-input-group">
              <div className="country-code">
                <span>+1</span>
              </div>
              <input
                type="tel"
                value={formatPhoneDisplay(phone)}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
                className="phone-input"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || phone.length < 10}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" className="btn-icon">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Send OTP via WhatsApp
                </>
              )}
            </button>

            <div className="alt-login">
              <span>or</span>
              <button
                type="button"
                className="email-login-btn"
                onClick={() => navigate('/login')}
              >
                Sign in with Email
              </button>
            </div>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="whatsapp-form">
            <div className="otp-info">
              <p>Enter the 6-digit code sent to</p>
              <strong>+1 {formatPhoneDisplay(phone)}</strong>
              <button
                type="button"
                className="change-number"
                onClick={() => {
                  setStep(1);
                  setOtp(['', '', '', '', '', '']);
                  setDemoOtp('');
                }}
              >
                Change
              </button>
            </div>

            {demoOtp && (
              <div className="demo-otp-banner">
                <span>Demo Mode - Your OTP is: </span>
                <strong>{demoOtp}</strong>
              </div>
            )}

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => otpRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className={`otp-input ${digit ? 'filled' : ''}`}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || otp.join('').length !== 6}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Verify OTP'
              )}
            </button>

            <div className="resend-section">
              {countdown > 0 ? (
                <p className="countdown">Resend OTP in {countdown}s</p>
              ) : (
                <button
                  type="button"
                  className="resend-btn"
                  onClick={handleResendOTP}
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </form>
        )}

        {/* Step 3: Name (New User) */}
        {step === 3 && (
          <form onSubmit={handleNameSubmit} className="whatsapp-form">
            <div className="welcome-new">
              <h2>Welcome to ONTAP!</h2>
              <p>Let's set up your account</p>
            </div>

            <div className="name-input-group">
              <label>What should we call you?</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder="Enter your name"
                className="name-input"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !name.trim()}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="whatsapp-login-footer">
          <p>By continuing, you agree to our</p>
          <div className="footer-links">
            <a href="/terms">Terms of Service</a>
            <span>&</span>
            <a href="/privacy">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLogin;
